"use client";

import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useQuery } from "@tanstack/react-query";
import { api } from '@/lib/api';
import { useAuth } from '../context/AuthContext';
import { Search, MoreHorizontal, Eye, FileText, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";

interface AuditLog {
  id: number;
  timestamp: string;
  eventType: string;
  deviceId: string | null;
  userId: string | null;
  sourceIp: string | null;
  details: Record<string, any>;
  severity: "info" | "warning" | "error" | "critical";
}

const severityColors: Record<"info" | "warning" | "error" | "critical", "default" | "warning" | "destructive" | "success"> = {
  info: "default",
  warning: "warning",
  error: "destructive",
  critical: "destructive",
} as const;

export function AuditLogsPage() {
  const { token } = useAuth();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const [filters, setFilters] = useState({
    type: "",
    deviceId: "",
    severity: "",
    startDate: "",
    endDate: "",
  });

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["audit-logs", page, limit, filters],
    queryFn: () => api.getAuditLogs({ limit, offset: (page - 1) * limit, ...filters, token: token! }),
    enabled: !!token,
  });

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const filteredLogs = data?.logs?.filter((log: any) => {
    if (search && !log.eventType.toLowerCase().includes(search.toLowerCase()) &&
        !log.deviceId?.toLowerCase().includes(search.toLowerCase()) &&
        !log.userId?.toLowerCase().includes(search.toLowerCase())) {
      return false;
    }
    return true;
  }) || [];

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card><CardContent className="pt-6"><div className="h-4 w-3/4 animate-pulse rounded bg-muted" /></CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Audit Logs</h1>
          <p className="text-muted-foreground">Monitor and investigate system activity</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>Log Entries</CardTitle>
            <Input placeholder="Search logs..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-64" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            <select value={filters.type} onChange={(e) => handleFilterChange("type", e.target.value)} className="flex h-10 w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">All Event Types</option>
              <option value="user_login">User Login</option>
              <option value="user_login_failed">Login Failed</option>
              <option value="device_updated">Device Updated</option>
              <option value="device_deleted">Device Deleted</option>
              <option value="group_created">Group Created</option>
              <option value="group_updated">Group Updated</option>
              <option value="group_deleted">Group Deleted</option>
              <option value="policy_created">Policy Created</option>
              <option value="policy_updated">Policy Updated</option>
              <option value="policy_deleted">Policy Deleted</option>
              <option value="api_token_created">API Token Created</option>
              <option value="api_token_deleted">API Token Deleted</option>
              <option value="client_config_created">Client Config Created</option>
              <option value="client_config_updated">Client Config Updated</option>
              <option value="client_config_deleted">Client Config Deleted</option>
              <option value="key_rotated">Key Rotated</option>
              <option value="services_restarted">Services Restarted</option>
              <option value="user_created">User Created</option>
              <option value="user_updated">User Updated</option>
              <option value="user_deleted">User Deleted</option>
            </select>
            <select value={filters.severity} onChange={(e) => handleFilterChange("severity", e.target.value)} className="flex h-10 w-[150px] rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">All Severities</option>
              <option value="info">Info</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
              <option value="critical">Critical</option>
            </select>
            <Input type="date" value={filters.startDate} onChange={(e) => handleFilterChange("startDate", e.target.value)} placeholder="Start Date" className="w-[160px]" />
            <Input type="date" value={filters.endDate} onChange={(e) => handleFilterChange("endDate", e.target.value)} placeholder="End Date" className="w-[160px]" />
            <Button variant="outline" onClick={() => setFilters({ type: "", deviceId: "", severity: "", startDate: "", endDate: "" })}>Clear Filters</Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead>
                <TableHead>Event Type</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Device ID</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Source IP</TableHead>
                <TableHead>Details</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredLogs.map((log: any) => (
                <TableRow key={log.id}>
                  <TableCell className="text-sm font-mono">{new Date(log.timestamp).toLocaleString()}</TableCell>
                  <TableCell className="font-medium">{log.eventType}</TableCell>
                  <TableCell>
                    <Badge variant={severityColors[log.severity as keyof typeof severityColors]}>{log.severity}</Badge>
                  </TableCell>
                  <TableCell className="font-mono text-sm">{log.deviceId || "-"}</TableCell>
                  <TableCell>{log.userId || "-"}</TableCell>
                  <TableCell className="font-mono text-sm">{log.sourceIp || "-"}</TableCell>
                  <TableCell>
                    <pre className="text-xs text-muted-foreground max-w-xs truncate">{JSON.stringify(log.details)}</pre>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => console.log("View details", log)}><Eye className="mr-2 h-4 w-4" />View Details</DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => console.log("Export", log)}><FileText className="mr-2 h-4 w-4" />Export Entry</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
              {filteredLogs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No log entries found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          <div className="flex items-center justify-between py-4">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, data?.total || 0)} of {data?.total || 0} entries
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || isFetching}>
                <ChevronLeft className="mr-2 h-4 w-4" />Previous
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * limit >= (data?.total || 0) || isFetching}>
                Next<ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}